import { createServerFn } from "@tanstack/react-start";
import { fetchFx } from "./gold-prices.functions";

type CachedSilver = {
  spotUsdPerOz: number;
  pkrPerUsd: number;
  inrPerUsd: number;
  fetchedAt: string;
};

let cache: CachedSilver | null = null;
const ONE_HOUR_MS = 10 * 60 * 1000;
const FETCH_TIMEOUT_MS = 3500;

async function fetchWithTimeout(url: string, timeoutMs = FETCH_TIMEOUT_MS): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

// Live XAG/USD spot. Primary: gold-api.com. Fallback: Yahoo Finance XAGUSD=X.
// Sanity-band check so a bad upstream can never poison the cache.
async function fetchSpotUsdPerOz(): Promise<number> {
  const isPlausible = (p: unknown): p is number =>
    typeof p === "number" && Number.isFinite(p) && p > 5 && p < 500;

  try {
    const r = await fetchWithTimeout("https://api.gold-api.com/price/XAG");
    if (r.ok) {
      const d = (await r.json()) as { price?: number };
      if (isPlausible(d?.price)) return d.price as number;
    }
  } catch {
    // fall through
  }

  try {
    const r = await fetchWithTimeout(
      "https://query1.finance.yahoo.com/v8/finance/chart/XAGUSD=X?interval=1m&range=1d",
    );
    if (r.ok) {
      const d = (await r.json()) as {
        chart?: { result?: Array<{ meta?: { regularMarketPrice?: number } }> };
      };
      const p = d?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (isPlausible(p)) return p as number;
    }
  } catch {
    // fall through
  }

  throw new Error("No live silver source available");
}

async function fetchFresh(): Promise<CachedSilver> {
  const spotUsdPerOz = await fetchSpotUsdPerOz();
  const fx = await fetchFx();
  return {
    spotUsdPerOz,
    pkrPerUsd: fx.PKR,
    inrPerUsd: fx.INR,
    fetchedAt: new Date().toISOString(),
  };
}

/**
 * 10-minute-cached live spot silver (XAG/oz) + USD FX.
 * Mirrors the gold engine cadence so both refresh on the same 10-minute cycle.
 */
export const getSilverRates = createServerFn({ method: "GET" }).handler(async () => {
  const now = Date.now();
  if (cache && now - new Date(cache.fetchedAt).getTime() < ONE_HOUR_MS) return cache;
  try {
    const fresh = await Promise.race<CachedSilver>([
      fetchFresh(),
      new Promise<CachedSilver>((_, reject) =>
        setTimeout(() => reject(new Error("silver ssr timeout")), FETCH_TIMEOUT_MS + 500),
      ),
    ]);
    cache = fresh;
    return cache;
  } catch {
    if (cache) return cache;
    return {
      spotUsdPerOz: 65,
      pkrPerUsd: 278,
      inrPerUsd: 83.5,
      fetchedAt: new Date().toISOString(),
    } satisfies CachedSilver;
  }
});